import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route } from 'react-router-dom';
import { getLocation, RouterRootState } from 'connected-react-router';
import { Location } from 'history';
import { InvalidPermissionsPage } from 'b2b-apps/shop/common/components/InvalidPermissionsPage';
import { UnloggedUserPage } from 'b2b-apps/shop/common/components/UnloggedUserPage';
import { createKey } from 'b2b-apps/shop/common/utils/key/key';
import { config } from 'b2b-apps/shop/config';
import { useEntriesMap } from 'b2b-apps/shop/content/hooks';
import { useUserData } from 'b2b-apps/shop/context/hooks';
import { useActiveLanguage } from 'b2b-apps/shop/i18n/hooks';
import { getAvailableLanguages } from 'b2b-apps/shop/i18n/utils/language';
import { RedirectionType, ROUTES, RouteType } from 'b2b-apps/shop/routes/components/Router';
import { REDIRECTIONS } from 'b2b-apps/shop/routes/components/Router/redirections';
import { useAccessCallbackData } from 'b2b-apps/shop/routes/hooks';
import { mapEntriesToRoutes } from 'b2b-apps/shop/routes/utils/mapEntriesToRoutes';
import { LANGUAGE_CODE } from 'b2b-common/core/i18n/consts';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const contentEntriesMap = useEntriesMap();
    const defaultLanguage = useActiveLanguage() || config.languages.default;
    const accessCallbackData = useAccessCallbackData();
    const { isLoggedIn } = useUserData();
    const location: Location = useSelector((state: RouterRootState) => getLocation(state));

    const renderWhenNotAllowed = (isLoggedIn: boolean) => function renderComponent() {
      if (!isLoggedIn) {
        return <UnloggedUserPage />;
      }

      return <InvalidPermissionsPage />;
    };

    const getRedirections = () => REDIRECTIONS.reduce(
      (redirections: ReactElement[], { when, from, ...redirection }: RedirectionType) => {
        if (when(accessCallbackData)) {
          const fromPaths = Array.isArray(from) ? from : [from];

          fromPaths.forEach((fromPath) => redirections.push(
            <Redirect
              {...redirection}
              from={fromPath}
              key={createKey('redirection', fromPath, redirection.to)}
            />,
          ));
        }

        return redirections;
      },
      [],
    );

    const getLocalisedRoutes = (language: string, routes: Record<string, any>) => Object.keys(routes).reduce(
      (
        routesComponents: ReactElement[],
        routeKey: string,
      ) => {
        const {
          allow,
          paths,
          component: Component,
          render,
          ...restRoute
        } = routes[routeKey] as RouteType;

        const path = paths[language];

        if (path) {
          const routeConfig = {
            ...restRoute,
            path,
            key: createKey('route', routeKey, paths[language], language),
            render: function renderComponent(routeProps: any) {
              if (Component) {
                return <Component {...routeProps} />;
              }

              if (render) {
                return render(routeProps);
              }

              return null;
            },
          };

          if (!allow(accessCallbackData)) {
            routeConfig.render = renderWhenNotAllowed(isLoggedIn);
          }

          routesComponents.push(<Route {...routeConfig} />);
        }

        return routesComponents;
      },
      [],
    );

    const fixingRedirections = () => {
      const redirections: ReactElement[] = Object.values(ROUTES).reduce(
        (redirections: ReactElement[], route: RouteType) => {
          const { paths, exact } = route;

          paths.fix && redirections.push(
            <Redirect
              exact={exact}
              from={paths.fix}
              to={{
                pathname: paths[defaultLanguage],
                search: location.search,
              }}
              key={createKey('redirect', paths.fix)}
            />,
          );

          return redirections;
        },
        [],
      );

      return redirections;
    };

    const contentRoutes = contentEntriesMap.isLoaded ? mapEntriesToRoutes(contentEntriesMap.data) : {};

    const routes = [
      ...getRedirections(),
      ...getAvailableLanguages().reduce(
        (acc, languageCode: LANGUAGE_CODE) => ([
          ...acc,
          ...getLocalisedRoutes(languageCode, ROUTES),
        ]),
        [],
      ),
      ...fixingRedirections(),
      ...getAvailableLanguages().reduce(
        (acc, languageCode: LANGUAGE_CODE) => ([
          ...acc,
          ...getLocalisedRoutes(languageCode, contentRoutes),
        ]),
        [],
      ),
    ];

    const injectedProps: InjectedProps = {
      contentEntriesMap,
      routes,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return WithContainer;
};

export { withContainer };
